import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import API from "../services/api";

const fmtMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("en-IN") : "—");
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString("en-IN") : "—");
const receiptStatusLabel = (status) =>
  status === "received" ? "Received Confirmed" : "Pending Confirmation";
const paymentProofName = (url = "", index = 0) => {
  const clean = String(url || "").split("/").pop() || "";
  return clean || `Proof ${index + 1}`;
};

const ContractorPaymentHistoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    paidCount: 0,
    paidAmount: 0,
    pendingCount: 0,
    pendingAmount: 0,
    receivedCount: 0,
    receivedAmount: 0,
    pendingReceiptCount: 0,
    pendingReceiptAmount: 0,
  });
  const [confirmModal, setConfirmModal] = useState({ show: false, row: null, note: "" });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get("/contractor/work-payments");
      setRows(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setSummary(res.data?.summary || {});
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const paidRows = useMemo(() => rows.filter((row) => row.paymentStatus === "paid"), [rows]);

  const confirmReceived = async () => {
    if (!confirmModal.row?._id) return;
    try {
      setSaving(true);
      setError("");
      await API.patch(`/contractor/work-payments/${confirmModal.row._id}/confirm-received`, {
        paymentReceivedNote: confirmModal.note,
      });
      setMessage("Payment receipt confirmed.");
      setConfirmModal({ show: false, row: null, note: "" });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to confirm payment receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="contractor-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <h4 className="mb-1">Payment History</h4>
            <div className="text-muted">Plant approved entries ka payment status aur accounts processing yahan dikhega.</div>
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {message ? <Alert variant="success">{message}</Alert> : null}

        <Row className="g-3 mb-4">
          <Col md={3}><div className="contractor-kpi"><div className="label">Paid Entries</div><div className="value">{summary.paidCount || 0}</div></div></Col>
          <Col md={3}><div className="contractor-kpi contractor-kpi-highlight"><div className="label">Paid Amount</div><div className="value">{fmtMoney(summary.paidAmount || 0)}</div></div></Col>
          <Col md={3}><div className="contractor-kpi"><div className="label">Pending Entries</div><div className="value">{summary.pendingCount || 0}</div></div></Col>
          <Col md={3}><div className="contractor-kpi"><div className="label">Pending Amount</div><div className="value">{fmtMoney(summary.pendingAmount || 0)}</div></div></Col>
          <Col md={3}><div className="contractor-kpi"><div className="label">Received Confirmed</div><div className="value">{summary.receivedCount || 0}</div></div></Col>
          <Col md={3}><div className="contractor-kpi contractor-kpi-highlight"><div className="label">Confirmed Amount</div><div className="value">{fmtMoney(summary.receivedAmount || 0)}</div></div></Col>
          <Col md={3}><div className="contractor-kpi"><div className="label">Receipt Pending</div><div className="value">{summary.pendingReceiptCount || 0}</div></div></Col>
          <Col md={3}><div className="contractor-kpi"><div className="label">Pending Confirm Amt</div><div className="value">{fmtMoney(summary.pendingReceiptAmount || 0)}</div></div></Col>
        </Row>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : paidRows.length === 0 ? (
          <div className="text-muted">Abhi tak koi contractor work payment process nahi hui.</div>
        ) : (
          <div className="contractor-table-wrap">
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Work Date</th>
                  <th>Worker</th>
                  <th>Work Type</th>
                  <th>No. Of Bags</th>
                  <th>Entry Amount</th>
                  <th>Paid Amount</th>
                  <th>Mode</th>
                  <th>Paid At</th>
                  <th>Processed By</th>
                  <th>Reference</th>
                  <th>Proofs</th>
                  <th>Receipt Status</th>
                  <th>Received At</th>
                  <th>Note</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paidRows.map((row) => (
                  <tr key={row._id}>
                    <td>{fmtDate(row.workDate)}</td>
                    <td>{row.workerName || "—"}</td>
                    <td>{row.workType || "—"}</td>
                    <td>{row.quantity || 0}</td>
                    <td>{fmtMoney(row.totalAmount)}</td>
                    <td>{fmtMoney(row.paymentAmount || row.totalAmount)}</td>
                    <td>{row.paymentMode || "—"}</td>
                    <td>{fmtDateTime(row.paymentPaidAt || row.paymentProcessedAt)}</td>
                    <td>{row.paymentProcessedBy?.fullName || "—"}</td>
                    <td>{row.paymentReference || "—"}</td>
                    <td>
                      {row.paymentProofs?.length ? row.paymentProofs.map((proof, proofIndex) => (
                        <div key={`${row._id}-proof-${proofIndex}`}>
                          <a href={proof} target="_blank" rel="noreferrer">{paymentProofName(proof, proofIndex)}</a>
                        </div>
                      )) : "—"}
                    </td>
                    <td>{receiptStatusLabel(row.paymentReceiptStatus)}</td>
                    <td>{fmtDateTime(row.paymentReceivedAt)}</td>
                    <td>{row.paymentReceivedNote || row.paymentNote || "—"}</td>
                    <td>
                      {row.paymentReceiptStatus !== "received" ? (
                        <Button
                          size="sm"
                          onClick={() => setConfirmModal({ show: true, row, note: row.paymentReceivedNote || "" })}
                        >
                          Confirm Received
                        </Button>
                      ) : (
                        <span className="text-success fw-semibold">Confirmed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>

      <Modal show={confirmModal.show} onHide={() => setConfirmModal({ show: false, row: null, note: "" })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Payment Receipt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="small text-muted">Amount</div>
            <strong>{fmtMoney(confirmModal.row?.totalAmount || 0)}</strong>
          </div>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={confirmModal.note}
              onChange={(e) => setConfirmModal((prev) => ({ ...prev, note: e.target.value }))}
              placeholder="Optional receive note"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmModal({ show: false, row: null, note: "" })}>
            Cancel
          </Button>
          <Button onClick={confirmReceived} disabled={saving}>
            {saving ? "Saving..." : "Confirm Received"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ContractorPaymentHistoryPage;
